import {useQueryParamState} from '@hconnect/adminconsole/src/common/QueryParamStateHook'
import {ResponsiveTable, Typography, useBreakPoints} from '@hconnect/uikit'
import {CanvasCard, PageHeader} from '@hm/fundamental'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'

import Content from '../../Molecules/Content'
import {ResultsForMessage} from '../../Molecules/ResultsForMessage'
import {CustomerStateType, selectCustomers} from '../../Organisms/Customers'
import {downloadDocument as downloadDocumentAction} from '../../Organisms/Documents/Documents.actions'
import {AppState} from '../../Root.store'

import {DocumentsColumns} from './Documents.columns'
import {CategoryTypes, DocumentsTableFilter} from './Documents.types'
import {DocumentsFilters} from './DocumentsFilters'
import {useDocuments} from './hooks/useDocuments'

type DocumentsComponentProps = {
  analytics: {
    userId?: string
    userMail?: string
    userCountry?: string
    userRoles?: any[]
    userIsInternal?: boolean
    customerId?: string
    customerName?: string
  }
}

export const DocumentsPage = ({analytics}: DocumentsComponentProps) => {
  const {t} = useTranslation()
  const dispatch = useDispatch()
  const screenSize = useBreakPoints()

  const [filter, setFilter] = useQueryParamState<DocumentsTableFilter>({
    rowsPerPage: 10,
    page: 1,
    sortColumn: 'createdAt',
    sortDirection: 'desc',
    category: CategoryTypes.ALL
  })
  const customersState = useSelector<AppState, CustomerStateType>((state) => selectCustomers(state))
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    customersState.selectedCustomer?.customerId || ''
  )

  const hasMultipleCustomers = customersState.customers.length > 1

  const setSortKey = (sortKey: string) => {
    const asc = !(filter.sortColumn === sortKey && filter.sortDirection === 'asc')
    setFilter({
      ...filter,
      sortColumn: sortKey,
      sortDirection: asc ? 'asc' : 'desc'
    })
  }

  const effectiveCustomerId = hasMultipleCustomers
    ? selectedCustomerId
    : customersState.selectedCustomer?.customerId

  const {data, isFetching, isError} = useDocuments({
    customerId: effectiveCustomerId,
    filter
  })
  const documentItems = (customersState.selectedCustomer?.customerId && data) || []
  const smallScreen = ['xs', 'sm'].includes(screenSize)
  const getEmptyMessage = (loading: boolean) => (loading ? t('loading') : t('noData'))
  const resultsForMessage = [customersState.selectedCustomer?.customerName || '']
  const downloadDocument = (jobId: string, documentId: string, fileName: string) =>
    dispatch(downloadDocumentAction(jobId, documentId, fileName))
  const columnConfig = DocumentsColumns({downloadDocument, analytics})

  return (
    <Content className="px-6 pb-6">
      <div className="[&_*]:!text-white">
        <PageHeader data-cy="page-features" className="pl-0 pr-0 md:pl-0 md:pr-0">
          Documents
        </PageHeader>
      </div>
      <div data-test-id="documents-filters-part">
        <DocumentsFilters
          filter={filter}
          setFilter={setFilter}
          hasMultipleCustomers={hasMultipleCustomers}
          selectedCustomerId={selectedCustomerId}
          onCustomerIdChange={setSelectedCustomerId}
        />
      </div>
      <CanvasCard className="pb-5 md:h-full md:mt-1.5 rounded-t-md rounded-b-md">
        {!isError ? (
          <>
            {!isFetching && !smallScreen ? (
              <ResultsForMessage messageList={resultsForMessage} />
            ) : null}
            {!smallScreen ? (
              <div className="px-0.5">
                <ResponsiveTable
                  rows={documentItems || []}
                  columns={columnConfig}
                  onSort={(_, sortKey) => {
                    setSortKey(sortKey)
                  }}
                  sortedBy={filter.sortColumn}
                  sortingOrder={filter.sortDirection}
                  loading={isFetching}
                  keyField="documentId"
                  emptyMessage={
                    <Typography variant="caption">{getEmptyMessage(isFetching)}</Typography>
                  }
                />
              </div>
            ) : (
              <div className="mx-0.5"></div>
            )}
          </>
        ) : null}
      </CanvasCard>
    </Content>
  )
}
